import React, { useEffect, useCallback } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import type { Socket } from 'socket.io-client'

import useGameStore from './store/GameStore'
import useUserStore from './store/UserStore'

import { SOCKET_EVENTS } from '@chessx/shared/socket'
import { API_CONTRACT_VERSION } from '@chessx/shared/api-contract'

import 'bootstrap/dist/css/bootstrap.min.css'
import './style.css'

import AdminLayout from './components/Layout/AdminLayout'
import AdminLogin from './pages/AdminLogin/AdminLogin'
import Dashboard from './pages/Dashboard'
import AdminHeroes from './pages/AdminHeroes'
import AdminUsers from './pages/AdminUsers/AdminUsers'
import AdminUsersDetails from './pages/AdminUsersDetails/AdminUsersDetails'
import OpeningTree from './pages/OpeningTree'
import ChessApiAnalytics from './pages/ChessApiAnalytics'
import Tournaments from './pages/Tournaments'
import AppConfig from './pages/AppConfig'
import Reconciliation from './pages/Reconciliation'
import Puzzles from './pages/Puzzles'
import PuzzleEditPage from './pages/Puzzles/PuzzleEditPage'

interface AppProps {
  socket: Socket
}

const App = ({ socket }: AppProps) => {
  const { setSocket } = useGameStore()
  const { user, token } = useUserStore()

  useEffect(() => {
    setSocket(socket)
  }, [])

  useEffect(() => {
    if (socket && user) {
      socket.auth = { userId: user?._id, token: token ?? '', wireVersion: API_CONTRACT_VERSION }
      socket.connect()
    }

    return () => {
      socket.off(SOCKET_EVENTS.ADMIN_GET_NO_USERS)
    }
  }, [user])

  const alertUser = useCallback(() => {
    if (socket) {
      if (user) {
        socket.emit(SOCKET_EVENTS.ADMIN_DISCONNECT_USER)
      }
      socket.disconnect()
    }
  }, [socket, user])

  useEffect(() => {
    window.addEventListener('beforeunload', alertUser)
    return () => window.removeEventListener('beforeunload', alertUser)
  }, [alertUser])

  return (
    <BrowserRouter>
      <div className="App">
        <Routes>
          <Route path="/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/detail" element={<AdminUsersDetails />} />
            <Route path="opening-tree" element={<OpeningTree />} />
            <Route path="heroes" element={<AdminHeroes />} />
            <Route path="chess-apis" element={<ChessApiAnalytics />} />
            <Route path="tournaments" element={<Tournaments />} />
            <Route path="puzzles" element={<Puzzles />} />
            <Route path="puzzles/:puzzleId" element={<PuzzleEditPage />} />
            <Route path="app-config" element={<AppConfig />} />
            <Route path="reconciliation" element={<Reconciliation />} />
          </Route>
          <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
        </Routes>
      </div>
    </BrowserRouter>
  )
}

export default App
